/**
 * Hole Visualisation Simulation Engine
 *
 * Simulates shot outcomes on a 2D hole layout using Monte Carlo sampling.
 * Each cell in the grid represents 1 metre. Shots follow a 2D normal
 * distribution (distance + lateral) with terrain-dependent modifiers.
 */

import shotConfig from '../data/shotConfig.json';
import { getHoleById } from './holeLoader';

// ── Terrain types ──────────────────────────────────────────────────────────

export const TERRAIN = {
  TEE: 'tee',
  FAIRWAY: 'fairway',
  ROUGH: 'rough',
  BUNKER: 'bunker',
  WATER: 'water',
  GREEN: 'green',
  OOB: 'oob',
};

const TERRAIN_COLOURS = {
  [TERRAIN.TEE]: '#8BC34A',
  [TERRAIN.FAIRWAY]: '#66BB6A',
  [TERRAIN.ROUGH]: '#388E3C',
  [TERRAIN.BUNKER]: '#D4B896',
  [TERRAIN.WATER]: '#42A5F5',
  [TERRAIN.GREEN]: '#9CCC65',
  [TERRAIN.OOB]: '#2E7D32',
};

export { TERRAIN_COLOURS };

// ── Per-club terrain modifiers from shotConfig.json ────────────────────────

const TERRAIN_MODIFIER_MATRIX = shotConfig.terrainModifiers;
const BETWEEN_CLUBS_THRESHOLD = shotConfig.betweenClubsThreshold;
const POWER_DOWN_SPREAD_PENALTY = shotConfig.powerDownSpreadPenalty;

/**
 * Look up [distanceMult, spreadMult] for a given terrain and club.
 * Falls back gracefully for unknown combos.
 */
function getTerrainModifier(terrain, clubName) {
  const terrainEntry = TERRAIN_MODIFIER_MATRIX[terrain];
  if (terrainEntry && terrainEntry[clubName]) {
    const [distanceMult, spreadMult] = terrainEntry[clubName];
    return { distanceMult, spreadMult };
  }
  // Fallback for water/OOB (shouldn't normally be shot from) or unknown
  if (terrain === TERRAIN.ROUGH || terrain === TERRAIN.WATER || terrain === TERRAIN.OOB) {
    return { distanceMult: 0.7, spreadMult: 1.8 };
  }
  return { distanceMult: 1.0, spreadMult: 1.0 };
}

// ── Deterministic noise ───────────────────────────────────────────────────

/**
 * Deterministic colour noise for a grid cell.
 * Returns a lightness offset in [-range, +range] based on a fast integer hash.
 * Supports optional two-octave mode for larger-scale variation.
 */
export function cellNoiseOffset(x, y, range = 8, twoOctave = false) {
  let h = (x * 374761 + y * 668265) | 0;
  h = ((h >> 16) ^ h) * 0x45d9f3b | 0;
  h = ((h >> 16) ^ h) * 0x45d9f3b | 0;
  h = (h >> 16) ^ h;
  let offset = ((h & 0xffff) / 0xffff) * 2 * range - range;

  if (twoOctave) {
    // Second octave at half frequency, half amplitude
    const hx = Math.floor(x / 2);
    const hy = Math.floor(y / 2);
    let h2 = (hx * 274861 + hy * 568365) | 0;
    h2 = ((h2 >> 16) ^ h2) * 0x45d9f3b | 0;
    h2 = ((h2 >> 16) ^ h2) * 0x45d9f3b | 0;
    h2 = (h2 >> 16) ^ h2;
    offset += ((h2 & 0xffff) / 0xffff) * range - range * 0.5;
  }

  return offset;
}

// ── Random sampling ────────────────────────────────────────────────────────

/** Box-Muller transform — returns a sample from N(0,1) */
function randn() {
  let u = 0, v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
}

// ── Default player stats ───────────────────────────────────────────────────

const DEFAULT_BRACKETS = {
  driver: { power: 230, distanceStdDev: 15, lateralStdDev: 18 },
  longIron: { power: 180, distanceStdDev: 12, lateralStdDev: 12 },
  midIron: { power: 150, distanceStdDev: 10, lateralStdDev: 8 },
  shortIron: { power: 110, distanceStdDev: 7, lateralStdDev: 5 },
  wedge: { power: 70, distanceStdDev: 5, lateralStdDev: 4 },
  pitch: { power: 40, distanceStdDev: 3, lateralStdDev: 3 },
};

const PUTT_STATS = {
  holeWithin: 1.5,
  makeRate2m: 0.85,
  makeRate5m: 0.40,
  makeRate10m: 0.12,
  missStdDev: 0.8,
};

export function createPlayerStats(overrides = {}) {
  const brackets = {};
  for (const [key, defaults] of Object.entries(DEFAULT_BRACKETS)) {
    brackets[key] = { ...defaults, ...(overrides[key] || {}) };
  }
  return {
    brackets,
    putt: { ...PUTT_STATS, ...(overrides.putt || {}) },
  };
}

export { DEFAULT_BRACKETS };

// ── Strategy archetypes from config ────────────────────────────────────────

export const STRATEGY_ARCHETYPES = shotConfig.strategyArchetypes;

// ── Club selection ─────────────────────────────────────────────────────────

/**
 * Sort brackets by power ascending. Cached per call.
 */
function sortedBrackets(brackets) {
  return Object.entries(brackets).sort((a, b) => a[1].power - b[1].power);
}

/**
 * Pick the best club for a given shot context and strategy.
 *
 * Shot contexts:
 *   tee      — first shot of hole
 *   approach — green is reachable
 *   layup    — green not reachable, on fairway/tee
 *   recovery — in rough or bunker
 *
 * Returns { name, ...stats, betweenClubs: boolean, poweredDown: boolean }
 */
function selectClub(targetDistance, brackets, shotContext, archetype) {
  const sorted = sortedBrackets(brackets);

  if (shotContext === 'recovery') {
    // Recovery: shortest club that can reach the fairway (target is fairway point)
    for (const [name, stats] of sorted) {
      if (stats.power >= targetDistance) return { name, ...stats, betweenClubs: false, poweredDown: false };
    }
    return { name: sorted[sorted.length - 1][0], ...sorted[sorted.length - 1][1], betweenClubs: false, poweredDown: false };
  }

  if (shotContext === 'approach') {
    // Approach: shortest club that reaches the green (precision matters)
    const effectiveRange = archetype.greenAttackRange;
    for (const [name, stats] of sorted) {
      if (stats.power * effectiveRange >= targetDistance) {
        return { name, ...stats, betweenClubs: false, poweredDown: false };
      }
    }
    // Nothing reaches — use longest available
    const longest = sorted[sorted.length - 1];
    return { name: longest[0], ...longest[1], betweenClubs: false, poweredDown: false };
  }

  // Tee or layup: use the longest allowed club
  const maxAllowedIdx = archetype.driverAllowed
    ? sorted.length - 1
    : sorted.length - 2; // skip driver (highest power)
  const maxIdx = Math.max(0, maxAllowedIdx);
  const chosen = sorted[maxIdx];

  // Between-clubs check: if target is between THRESHOLD and 100% of a smaller club
  if (maxIdx > 0) {
    const smallerClub = sorted[maxIdx - 1];
    const smallerPower = smallerClub[1].power;
    if (targetDistance >= smallerPower * BETWEEN_CLUBS_THRESHOLD && targetDistance <= smallerPower) {
      if (archetype.betweenClubs === 'clubDown') {
        // Conservative: use smaller club at full power
        return { name: smallerClub[0], ...smallerClub[1], betweenClubs: true, poweredDown: false };
      }
      // Aggressive: power down the larger club (spread penalty applied in simulateShot)
      return { name: chosen[0], ...chosen[1], betweenClubs: true, poweredDown: true };
    }
  }

  return { name: chosen[0], ...chosen[1], betweenClubs: false, poweredDown: false };
}

// ── Hole layout ────────────────────────────────────────────────────────────

export function createHoleLayout(config) {
  const { length, width, zones, par, pinX, pinY, teeX, teeY, dropZones, waypoints } = config;
  const grid = Array.from({ length: width }, () =>
    Array.from({ length }, () => TERRAIN.OOB)
  );

  for (const zone of zones) {
    paintZone(grid, zone, length, width);
  }

  return {
    grid,
    length,
    width,
    par,
    pin: { x: pinX, y: pinY },
    tee: { x: teeX, y: teeY },
    dropZones: dropZones || [],
    waypoints: waypoints || [],
  };
}

function paintZone(grid, zone, holeLength, holeWidth) {
  const { type, shape } = zone;
  if (shape.kind === 'rect') {
    const { x1, y1, x2, y2 } = shape;
    for (let y = Math.max(0, y1); y <= Math.min(holeWidth - 1, y2); y++) {
      for (let x = Math.max(0, x1); x <= Math.min(holeLength - 1, x2); x++) {
        grid[y][x] = type;
      }
    }
  } else if (shape.kind === 'ellipse') {
    const { cx, cy, rx, ry } = shape;
    for (let y = Math.max(0, Math.floor(cy - ry)); y <= Math.min(holeWidth - 1, Math.ceil(cy + ry)); y++) {
      for (let x = Math.max(0, Math.floor(cx - rx)); x <= Math.min(holeLength - 1, Math.ceil(cx + rx)); x++) {
        const dx = (x - cx) / rx;
        const dy = (y - cy) / ry;
        if (dx * dx + dy * dy <= 1) {
          grid[y][x] = type;
        }
      }
    }
  } else if (shape.kind === 'polygon') {
    const { points } = shape;
    const minX = Math.max(0, Math.floor(Math.min(...points.map(p => p.x))));
    const maxX = Math.min(holeLength - 1, Math.ceil(Math.max(...points.map(p => p.x))));
    const minY = Math.max(0, Math.floor(Math.min(...points.map(p => p.y))));
    const maxY = Math.min(holeWidth - 1, Math.ceil(Math.max(...points.map(p => p.y))));
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        if (pointInPolygon(x, y, points)) {
          grid[y][x] = type;
        }
      }
    }
  }
}

function pointInPolygon(x, y, points) {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const xi = points[i].x, yi = points[i].y;
    const xj = points[j].x, yj = points[j].y;
    const intersect = ((yi > y) !== (yj > y)) &&
      (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

// ── Shot simulation ────────────────────────────────────────────────────────

/**
 * Simulate a single shot with enriched output.
 *
 * @param {Object} from - { x, y } position in metres
 * @param {Object} target - { x, y } aim point
 * @param {Object} playerStats - from createPlayerStats()
 * @param {string} terrain - current terrain type (terrain shot is played FROM)
 * @param {Object} hole - hole layout
 * @param {string} shotContext - 'tee' | 'approach' | 'layup' | 'recovery'
 * @param {Object} archetype - strategy archetype config
 * @returns {Object} enriched shot result
 */
export function simulateShot(from, target, playerStats, terrain, hole, shotContext = 'layup', archetype = null) {
  const dx = target.x - from.x;
  const dy = target.y - from.y;
  const targetDist = Math.sqrt(dx * dx + dy * dy);
  const angle = Math.atan2(dy, dx);

  // On green -> putt
  if (terrain === TERRAIN.GREEN) {
    const puttResult = simulatePutt(from, hole.pin, targetDist, playerStats.putt, hole);
    return {
      ...puttResult,
      intendedDistance: targetDist,
      actualDistance: puttResult.holed ? targetDist : Math.sqrt((puttResult.land.x - from.x) ** 2 + (puttResult.land.y - from.y) ** 2),
      lateralOffset: 0,
      distanceError: 0,
      fromTerrain: terrain,
      terrainModifier: { distanceMult: 1.0, spreadMult: 1.0 },
      shotContext: 'putt',
      penaltyReason: puttResult.penaltyReason || null,
      originalLand: puttResult.originalLand || puttResult.land,
    };
  }

  // Select club based on context
  const effectiveArchetype = archetype || STRATEGY_ARCHETYPES.aggressive;
  const club = selectClub(targetDist, playerStats.brackets, shotContext, effectiveArchetype);
  const terrainMod = getTerrainModifier(terrain, club.name);

  // Between-clubs power-down spread penalty
  const spreadPenalty = club.poweredDown ? POWER_DOWN_SPREAD_PENALTY : 1.0;

  // Intended distance: capped at club power * terrain modifier
  const maxDist = club.power * terrainMod.distanceMult;
  const intendedDist = Math.min(targetDist, maxDist);

  // Sample actual distance (normal, clipped to > 0)
  const actualDist = Math.max(5, intendedDist + randn() * club.distanceStdDev * terrainMod.distanceMult);

  // Sample lateral offset
  const lateralOffset = randn() * club.lateralStdDev * terrainMod.spreadMult * spreadPenalty;

  // Convert to x,y
  const landX = Math.round(from.x + Math.cos(angle) * actualDist - Math.sin(angle) * lateralOffset);
  const landY = Math.round(from.y + Math.sin(angle) * actualDist + Math.cos(angle) * lateralOffset);

  const landing = resolveLanding(landX, landY, hole, club.name);

  return {
    ...landing,
    intendedDistance: Math.round(intendedDist),
    actualDistance: Math.round(actualDist),
    lateralOffset: Math.round(lateralOffset * 10) / 10,
    distanceError: Math.round(actualDist - intendedDist),
    fromTerrain: terrain,
    terrainModifier: terrainMod,
    shotContext,
    // penaltyReason and originalLand come from landing via spread
  };
}

function simulatePutt(from, pin, distance, puttStats, hole) {
  if (distance <= puttStats.holeWithin) {
    return { land: { ...pin }, terrain: TERRAIN.GREEN, penalty: 0, holed: true, club: 'putter' };
  }

  let makeProb;
  if (distance <= 2) makeProb = puttStats.makeRate2m;
  else if (distance <= 5) makeProb = puttStats.makeRate2m + (puttStats.makeRate5m - puttStats.makeRate2m) * ((distance - 2) / 3);
  else if (distance <= 10) makeProb = puttStats.makeRate5m + (puttStats.makeRate10m - puttStats.makeRate5m) * ((distance - 5) / 5);
  else makeProb = Math.max(0.02, puttStats.makeRate10m * (10 / distance));

  if (Math.random() < makeProb) {
    return { land: { ...pin }, terrain: TERRAIN.GREEN, penalty: 0, holed: true, club: 'putter' };
  }

  const residual = Math.max(0.3, distance * 0.15 + randn() * puttStats.missStdDev);
  const missAngle = Math.random() * 2 * Math.PI;
  const landX = Math.round(pin.x + Math.cos(missAngle) * residual);
  const landY = Math.round(pin.y + Math.sin(missAngle) * residual);

  return resolveLanding(landX, landY, hole, 'putter');
}

/**
 * Resolve where a shot actually ends up.
 *
 * PGA-style drop rules (simplified):
 *   Water hazard → 1 stroke penalty, drop at nearest drop zone
 *   OOB / off grid → 1 stroke penalty, drop at nearest drop zone
 *     (real OOB is stroke-and-distance; we use drop zones for simplicity)
 *
 * Returns:
 *   land          — { x, y } where the player plays next
 *   terrain       — terrain at the drop/landing point (what they play FROM next)
 *   penalty       — 0 or 1
 *   penaltyReason — null | 'water' | 'oob' — what the ball actually hit
 *   originalLand  — { x, y } where the ball actually went (before drop)
 *   holed         — boolean
 *   club          — club name (pass-through)
 */
function resolveLanding(landX, landY, hole, clubName) {
  // Off the grid entirely → OOB
  if (landX < 0 || landX >= hole.length || landY < 0 || landY >= hole.width) {
    const drop = findNearestDropZone(landX, landY, hole);
    return {
      land: drop, terrain: hole.grid[drop.y][drop.x],
      penalty: 1, penaltyReason: TERRAIN.OOB,
      originalLand: { x: landX, y: landY },
      holed: false, club: clubName,
    };
  }

  const landTerrain = hole.grid[landY][landX];

  if (landTerrain === TERRAIN.WATER || landTerrain === TERRAIN.OOB) {
    const drop = findNearestDropZone(landX, landY, hole);
    return {
      land: drop, terrain: hole.grid[drop.y][drop.x],
      penalty: 1, penaltyReason: landTerrain,
      originalLand: { x: landX, y: landY },
      holed: false, club: clubName,
    };
  }

  const distToPin = Math.sqrt((landX - hole.pin.x) ** 2 + (landY - hole.pin.y) ** 2);
  const holed = landTerrain === TERRAIN.GREEN && distToPin < 0.5;

  return {
    land: { x: landX, y: landY }, terrain: landTerrain,
    penalty: 0, penaltyReason: null,
    originalLand: { x: landX, y: landY },
    holed, club: clubName,
  };
}

function findNearestDropZone(x, y, hole) {
  if (hole.dropZones && hole.dropZones.length > 0) {
    let best = hole.dropZones[0];
    let bestDist = Infinity;
    for (const dz of hole.dropZones) {
      const d = (dz.x - x) ** 2 + (dz.y - y) ** 2;
      if (d < bestDist) { bestDist = d; best = dz; }
    }
    return { x: best.x, y: best.y };
  }
  const clampedX = Math.max(0, Math.min(hole.length - 1, x));
  const midY = Math.floor(hole.width / 2);
  return { x: clampedX, y: midY };
}

// ── Shot context detection ────────────────────────────────────────────────

function determineShotContext(pos, terrain, hole, player, archetype) {
  if (terrain === TERRAIN.GREEN) return 'putt';

  const distToPin = Math.sqrt((hole.pin.x - pos.x) ** 2 + (hole.pin.y - pos.y) ** 2);
  const maxPower = getMaxPower(player.brackets, archetype);

  // On the tee but green is reachable (e.g. par 3): treat as approach so
  // club selection picks the shortest club that reaches rather than the longest allowed.
  if (terrain === TERRAIN.TEE) {
    if (distToPin <= maxPower * archetype.greenAttackRange) return 'approach';
    return 'tee';
  }

  // In rough or bunker but close enough to attack the green — treat as approach, not recovery
  if (terrain === TERRAIN.ROUGH || terrain === TERRAIN.BUNKER) {
    if (distToPin <= maxPower * archetype.greenAttackRange) return 'approach';
    return 'recovery';
  }

  // On fairway: check if green is reachable
  if (distToPin <= maxPower * archetype.greenAttackRange) return 'approach';

  return 'layup';
}

function getMaxPower(brackets, archetype) {
  const sorted = sortedBrackets(brackets);
  if (archetype.driverAllowed) {
    return sorted[sorted.length - 1][1].power;
  }
  // No driver: second-longest
  const idx = Math.max(0, sorted.length - 2);
  return sorted[idx][1].power;
}

// ── Strategy / full hole simulation ────────────────────────────────────────

/**
 * Simulate an entire hole.
 * @param {Object} hole - from createHoleLayout
 * @param {Object} player - from createPlayerStats
 * @param {string} strategyKey - key into STRATEGY_ARCHETYPES
 * @returns {{ shots: Array, score: number, toPar: number }}
 */
export function simulateHole(hole, player, strategyKey = 'aggressive') {
  const archetype = STRATEGY_ARCHETYPES[strategyKey] || STRATEGY_ARCHETYPES.aggressive;
  const shots = [];
  let pos = { ...hole.tee };
  let currentTerrain = TERRAIN.TEE;
  let strokes = 0;
  let swingNumber = 0;
  const maxStrokes = hole.par + 5;

  while (strokes < maxStrokes) {
    strokes++;
    swingNumber++;
    const shotContext = determineShotContext(pos, currentTerrain, hole, player, archetype);
    const target = pickTarget(pos, currentTerrain, hole, player, archetype, shotContext);
    const result = simulateShot(pos, target, player, currentTerrain, hole, shotContext, archetype);

    shots.push({
      shotNumber: swingNumber,
      from: { ...pos },
      to: result.land,
      originalLand: result.originalLand,
      target: { ...target },
      terrain: result.terrain,
      club: result.club,
      penalty: result.penalty,
      penaltyReason: result.penaltyReason,
      holed: result.holed,
      intendedDistance: result.intendedDistance,
      actualDistance: result.actualDistance,
      lateralOffset: result.lateralOffset,
      distanceError: result.distanceError,
      fromTerrain: result.fromTerrain,
      terrainModifier: result.terrainModifier,
      shotContext: result.shotContext,
    });

    strokes += result.penalty;

    if (result.holed) break;
    pos = result.land;
    currentTerrain = result.terrain;
  }

  return {
    shots,
    score: strokes,
    toPar: strokes - hole.par,
  };
}

/**
 * Pick target based on shot context and strategy archetype.
 * Waypoints define a corridor — target is the furthest reachable point along it.
 */
function pickTarget(pos, terrain, hole, player, archetype, shotContext) {
  const distToPin = Math.sqrt((hole.pin.x - pos.x) ** 2 + (hole.pin.y - pos.y) ** 2);
  const maxPower = getMaxPower(player.brackets, archetype);

  // On green -> putt at pin
  if (terrain === TERRAIN.GREEN) return { ...hole.pin };

  // Recovery: aim for nearest safe fairway point
  if (shotContext === 'recovery') {
    return findRecoveryTarget(pos, hole);
  }

  // Approach: aim at pin
  if (shotContext === 'approach') {
    return { ...hole.pin };
  }

  // Tee or layup: corridor-based targeting
  if (archetype.cutCorners && distToPin <= maxPower) {
    // Aggressive corner-cutting: try to reach pin directly if midpoint is safe
    const midX = Math.round((pos.x + hole.pin.x) / 2);
    const midY = Math.round((pos.y + hole.pin.y) / 2);
    if (midX >= 0 && midX < hole.length && midY >= 0 && midY < hole.width) {
      const midTerrain = hole.grid[midY][midX];
      if (midTerrain !== TERRAIN.OOB && midTerrain !== TERRAIN.WATER) {
        return { ...hole.pin };
      }
    }
  }

  // Follow corridor: find furthest reachable point along waypoint path
  return findCorridorTarget(pos, hole, maxPower);
}

/**
 * Find the furthest reachable point along the waypoint corridor.
 * The corridor runs: current position -> waypoints in order -> pin.
 */
function findCorridorTarget(pos, hole, maxPower) {
  const path = [];

  // Build ordered path of remaining waypoints + pin
  if (hole.waypoints && hole.waypoints.length > 0) {
    // Include waypoints that are closer to the pin than the player currently is.
    // Using distance-to-pin rather than raw x comparison so dogleg holes work correctly.
    const distToPin = Math.sqrt((hole.pin.x - pos.x) ** 2 + (hole.pin.y - pos.y) ** 2);
    for (const wp of hole.waypoints) {
      const wpDistToPin = Math.sqrt((hole.pin.x - wp.x) ** 2 + (hole.pin.y - wp.y) ** 2);
      if (wpDistToPin < distToPin - 5) {
        path.push(wp);
      }
    }
  }
  path.push(hole.pin);

  // Walk along path segments, find furthest reachable point
  let totalDist = 0;
  let from = pos;

  for (const waypoint of path) {
    const segDx = waypoint.x - from.x;
    const segDy = waypoint.y - from.y;
    const segLen = Math.sqrt(segDx * segDx + segDy * segDy);

    if (totalDist + segLen <= maxPower) {
      // Can reach this waypoint — continue to next segment
      totalDist += segLen;
      from = waypoint;
    } else {
      // Can't reach this waypoint — interpolate along segment
      const remaining = maxPower - totalDist;
      if (remaining <= 0) break;
      const t = remaining / segLen;
      return {
        x: Math.round(from.x + segDx * t),
        y: Math.round(from.y + segDy * t),
      };
    }
  }

  // Can reach the pin directly (or past all waypoints)
  return { ...hole.pin };
}

/**
 * Recovery: find nearest fairway cell as target.
 */
function findRecoveryTarget(pos, hole) {
  let bestX = pos.x;
  let bestY = pos.y;
  let bestDist = Infinity;

  // Search a reasonable radius around current position
  const searchRadius = 80;
  const minX = Math.max(0, Math.floor(pos.x - searchRadius));
  const maxX = Math.min(hole.length - 1, Math.ceil(pos.x + searchRadius));
  const minY = Math.max(0, Math.floor(pos.y - searchRadius));
  const maxY = Math.min(hole.width - 1, Math.ceil(pos.y + searchRadius));

  // Sample every 3rd cell for performance
  for (let y = minY; y <= maxY; y += 3) {
    for (let x = minX; x <= maxX; x += 3) {
      if (hole.grid[y][x] === TERRAIN.FAIRWAY) {
        const d = (x - pos.x) ** 2 + (y - pos.y) ** 2;
        // Prefer fairway cells that are ahead (towards pin)
        const forwardBonus = (x > pos.x) ? -500 : 0;
        if (d + forwardBonus < bestDist) {
          bestDist = d + forwardBonus;
          bestX = x;
          bestY = y;
        }
      }
    }
  }

  return { x: bestX, y: bestY };
}

// ── Batch simulation ───────────────────────────────────────────────────────

/**
 * Run N simulations and collect results with hazard indices.
 */
export function simulateMultipleHoles(hole, player, strategy, count = 500) {
  const results = [];
  const landingsByShot = {};
  const scores = {};
  const resultsByScore = {};
  // Track which result indices hit each hazard (using Sets for O(1) dedup)
  const bunkerSet = new Set();
  const waterSet = new Set();
  const oobSet = new Set();

  for (let i = 0; i < count; i++) {
    const result = simulateHole(hole, player, strategy);
    results.push(result);

    const label = scoreLabel(result.toPar);
    scores[label] = (scores[label] || 0) + 1;
    if (!resultsByScore[label]) resultsByScore[label] = [];
    resultsByScore[label].push(i);

    for (const shot of result.shots) {
      const key = shot.shotNumber;
      if (!landingsByShot[key]) landingsByShot[key] = [];
      landingsByShot[key].push({
        x: shot.to.x, y: shot.to.y,
        fromX: shot.from.x, fromY: shot.from.y,
        targetX: shot.target.x, targetY: shot.target.y,
        terrain: shot.terrain,
        fromTerrain: shot.fromTerrain,
        penaltyReason: shot.penaltyReason,
        club: shot.club,
        resultIndex: i,
      });

      // Hazard tracking using the actual penalty reason
      if (shot.penaltyReason === TERRAIN.WATER) waterSet.add(i);
      if (shot.penaltyReason === TERRAIN.OOB) oobSet.add(i);
      if (shot.terrain === TERRAIN.BUNKER) bunkerSet.add(i);
    }
  }

  const hazardHits = {
    bunker: [...bunkerSet],
    water: [...waterSet],
    oob: [...oobSet],
  };

  const scoreDistribution = {};
  for (const [label, cnt] of Object.entries(scores)) {
    scoreDistribution[label] = ((cnt / count) * 100).toFixed(1);
  }

  const avgScore = results.reduce((s, r) => s + r.score, 0) / count;

  const aimPoints = results.length > 0
    ? results[0].shots.map(s => ({ x: s.target.x, y: s.target.y }))
    : [];

  return { results, landingsByShot, scoreDistribution, avgScore, resultsByScore, aimPoints, hazardHits };
}

function scoreLabel(toPar) {
  if (toPar <= -2) return 'Eagle+';
  if (toPar === -1) return 'Birdie';
  if (toPar === 0) return 'Par';
  if (toPar === 1) return 'Bogey';
  if (toPar === 2) return 'Double';
  return 'Triple+';
}

export const SCORE_ORDER = ['Eagle+', 'Birdie', 'Par', 'Bogey', 'Double', 'Triple+'];
export const SCORE_COLOURS = {
  'Eagle+': '#FFD700',
  'Birdie': '#EF4444',
  'Par': '#22C55E',
  'Bogey': '#3B82F6',
  'Double': '#8B5CF6',
  'Triple+': '#6B7280',
};

// ── Default par 4 hole ─────────────────────────────────────────────────────
// Hole data now lives in JSON files under golf/data/holes/.
// Re-exported here for backward compatibility.

export const DEFAULT_PAR4 = getHoleById('dogleg-right-par4');

// ── Putting reference data ──────────────────────────────────────────────────

export const PUTTING_REFERENCE_DATA = [
  { distanceFt: 3,  distanceM: 0.9, pga: 96, amateur: 85, model: 85, label: '3 ft (0.9m)' },
  { distanceFt: 5,  distanceM: 1.5, pga: 77, amateur: 47, model: 85, label: '5 ft (1.5m)' },
  { distanceFt: 8,  distanceM: 2.4, pga: 56, amateur: 35, model: 62, label: '8 ft (2.4m)' },
  { distanceFt: 10, distanceM: 3.0, pga: 40, amateur: 30, model: 55, label: '10 ft (3m)' },
  { distanceFt: 15, distanceM: 4.6, pga: 23, amateur: 20, model: 33, label: '15 ft (4.6m)' },
  { distanceFt: 20, distanceM: 6.1, pga: 15, amateur: 15, model: 22, label: '20 ft (6.1m)' },
  { distanceFt: 30, distanceM: 9.1, pga: 7,  amateur: 7,  model: 13, label: '30 ft (9.1m)' },
];

export const PUTTING_SOURCES = [
  { name: 'Shot Scope', url: 'https://shotscope.com/blog/stats/putting-statistics/' },
  { name: 'Arccos / MyGolfSpy', url: 'https://mygolfspy.com/news-opinion/the-real-story-of-amateur-golf/' },
  { name: 'PGA ShotLink', url: 'https://www.pgatour.com/stats' },
  { name: 'Gelman & Nolan', url: 'https://statmodeling.stat.columbia.edu/wp-content/uploads/2020/04/golf_chapter.pdf' },
];
